import { NavLink } from 'react-router';
import type { RefObject } from 'react';
import { useAuth } from '../lib/auth-context.js';
import { useAdminCounts } from '../hooks/useAdminCounts.js';
import { AppMark, APP_NAME } from './shell/AppMark.js';
import { PersonBlock } from './shell/PersonBlock.js';

interface NavItem {
  to: string;
  label: string;
  /** Boxicon name, e.g. `bx-grid-alt` — rendered as `<i class="bx {icon}">`. */
  icon: string;
  /**
   * Phase 9 Session A — optional key that maps to a count from
   * `useAdminCounts()`. When present and > 0, a rose badge is rendered
   * inline with the NavLink label.
   */
  badgeKey?: 'candidatesPending' | 'suggestionsPending' | 'proposalsPending';
  /** A stable test hook for an item that gates key on (e.g. How it works). */
  testId?: string;
  /**
   * Spec 247 — where the destination is SHOWN. Omitted = the sidebar.
   * `'account'` = the account menu behind the bar's disc (Settings).
   *
   * The item stays in this table either way, because BarCrumb and the tab
   * title resolve a page's name from here — "a destination has one name in
   * the app, not two that can drift." Moving Settings out of the table would
   * have silently stripped /settings of its crumb and its tab title.
   */
  placement?: 'account';
}

interface NavGroup {
  label: string;
  items: NavItem[];
  /**
   * Spec 088c — role-gate the entire group. Any-of semantics: the group is
   * rendered only if the user has at least one of the listed roles. If
   * omitted, the group is visible to all authenticated users.
   */
  requireRoles?: string[];
  /**
   * Stable identifier used in the `sidebar-group-…` data-testid. Defaults to
   * `label` when omitted. Useful when a label is renamed for UX (e.g.
   * Admin → Observatory) but tests still need a stable hook.
   */
  testIdLabel?: string;
}

/**
 * Spec 247 — the nav follows the product's own loop: Monitor -> Analyze -> Act
 * -> Measure (CLAUDE.md: "Monitor -> Analyze -> Generate Playbooks -> Track
 * Impact"), plus Observatory for admins.
 *
 * It had eight groups — General, Sources, Optimize, Actions, Measure, Project,
 * Manage, Observatory — holding 1,483px of nav in 711px of height, with items
 * cut off behind the person block (design review 2026-09-27, P1-12). Kridt's
 * limit is five, and it counts what an ADMIN sees, so Settings moved to the
 * account menu (placement: 'account') rather than keeping a sixth group.
 *
 * "How it works" is named exactly that and sits last (Kridt layout.md). It was
 * the FIRST item, wrapping onto two lines as "How GEO Observatory works".
 */
export const navGroups: NavGroup[] = [
  {
    label: 'Monitor',
    // Stable hook: gates 088c and 229 key on `sidebar-group-General`, and this
    // group holds exactly General's items plus Schedule — its successor.
    testIdLabel: 'General',
    items: [
      { to: '/overview', label: 'Overview', icon: 'bx-grid-alt' },
      { to: '/collection', label: 'Collection', icon: 'bx-data' },
      { to: '/schedule', label: 'Schedule', icon: 'bx-time-five' },
      { to: '/prompts', label: 'Prompts', icon: 'bx-file' },
      { to: '/responses', label: 'Responses', icon: 'bx-message-rounded' },
    ],
  },
  {
    label: 'Analyze',
    items: [
      { to: '/sources', label: 'Domains', icon: 'bx-globe' },
      { to: '/keywords', label: 'Keywords', icon: 'bx-search' },
      { to: '/sources/urls', label: 'URLs', icon: 'bx-link' },
      { to: '/coverage', label: 'Coverage', icon: 'bx-layer' },
      { to: '/fanout', label: 'Fan-out', icon: 'bx-sitemap' },
      { to: '/personas', label: 'Personas', icon: 'bx-user' },
      { to: '/persona-evaluations', label: 'AI-answer persona-fit', icon: 'bx-comment-detail' },
      { to: '/page-persona-evaluations', label: 'Page persona-fit', icon: 'bx-file-find' },
      { to: '/persona-diagnoses', label: 'Persona diagnosis', icon: 'bx-network-chart' },
    ],
  },
  {
    label: 'Act',
    items: [
      { to: '/playbooks', label: 'Playbooks', icon: 'bx-book' },
      { to: '/actions/earned', label: 'Earned', icon: 'bx-bolt-circle' },
      { to: '/actions/owned', label: 'Owned', icon: 'bx-target-lock' },
      { to: '/llms-txt', label: 'llms.txt', icon: 'bx-file-blank' },
    ],
  },
  {
    label: 'Measure',
    items: [
      { to: '/impact', label: 'Impact', icon: 'bx-trending-up' },
      { to: '/how-it-works', label: 'How it works', icon: 'bx-info-circle', testId: 'sidebar-how-it-works' },
      // Shown in the account menu; kept here so its name resolves in one place.
      { to: '/settings', label: 'Settings', icon: 'bx-cog', placement: 'account' },
    ],
  },
  {
    label: 'Observatory',
    testIdLabel: 'Admin',
    requireRoles: ['rule_admin', 'orbit:admin'],
    items: [
      { to: '/admin', label: 'Home', icon: 'bx-bar-chart-alt-2' },
      { to: '/admin/rules', label: 'Rule library', icon: 'bx-book-open' },
      {
        to: '/admin/rules/candidates',
        label: 'Rule candidates',
        icon: 'bx-bulb',
        badgeKey: 'candidatesPending',
      },
      {
        to: '/admin/rules/suggestions',
        label: 'Rule suggestions',
        icon: 'bx-git-pull-request',
        badgeKey: 'suggestionsPending',
      },
      {
        // Spec 103 — clustered candidates -> proposed new rules.
        to: '/admin/rules/proposals',
        label: 'Rule proposals',
        icon: 'bx-git-merge',
        badgeKey: 'proposalsPending',
      },
      { to: '/admin/rule-sources', label: 'Rule sources', icon: 'bx-rss' },
      { to: '/admin/evaluations', label: 'Page evaluations', icon: 'bx-search-alt' },
      { to: '/admin/feature-flags', label: 'Feature flags', icon: 'bx-toggle-right' },
    ],
  },
];

interface SidebarProps {
  /** Whether the small-screen drawer is open — stamped as `data-open`. */
  drawerOpen: boolean;
  /** The drawer's panel ref (from `useDrawer`), for outside-click and focus handling. */
  navRef: RefObject<HTMLElement | null>;
}

/**
 * Spec 229 — the nav is a region of the sheet, not an always-dark panel.
 *
 * The dark ink-on-ink chrome (and the `!important` modifiers it needed to
 * beat `tokens.css`'s unlayered `a { color }` rule) is gone: `tokens.css` is
 * now layered, so `.hub-nav-item[aria-current="page"]` wins on specificity
 * within the same layer without help. The only ink block left is the current
 * item, via React Router's own `aria-current="page"` on the active NavLink.
 */
export function Sidebar({ drawerOpen, navRef }: SidebarProps) {
  const { hasRole } = useAuth();

  // Spec 088c — filter out groups whose `requireRoles` the user does not
  // satisfy. Omitted groups are removed from the DOM (not just hidden).
  const visibleGroups = navGroups.filter((group) => {
    if (!group.requireRoles || group.requireRoles.length === 0) return true;
    return group.requireRoles.some((r) => hasRole(r));
  });

  // Phase 9 Session A (gap XC-4) — pending counts for the Admin group.
  // Only mount the polling hook for users who can actually see the Admin
  // group; otherwise the 401 on these admin-gated endpoints would trigger
  // an unwanted redirect to the sign-in sheet for regular users.
  const showAdminBadges =
    hasRole('rule_admin') || hasRole('orbit:admin');

  return (
    <aside
      className="hub-shell__nav"
      data-open={drawerOpen ? 'true' : undefined}
      ref={navRef}
      data-testid="shell-nav"
    >
      {/* Spec 247 — Kridt's brand: the app mark and the name on one line, 600. */}
      <div className="hub-shell__brand" data-testid="shell-brand">
        <AppMark />
        {APP_NAME}
      </div>
      <nav className="hub-shell__menu sidebar-scroll" aria-label="Main">
        {visibleGroups.map((group) => (
          <div key={group.label} data-testid={`sidebar-group-${group.testIdLabel ?? group.label}`}>
            <div className="hub-nav-group">{group.label}</div>
            {group.items
              .filter((item) => item.placement !== 'account')
              .map((item) =>
                showAdminBadges && item.badgeKey ? (
                  <AdminBadgedNavItem key={item.to} item={item} />
                ) : (
                  <NavLink key={item.to} to={item.to} className="hub-nav-item" data-testid={item.testId}>
                    <i className={`bx ${item.icon}`} aria-hidden="true" />
                    {item.label}
                  </NavLink>
                )
              )}
          </div>
        ))}
      </nav>
      <div className="hub-shell__me">
        <PersonBlock />
      </div>
    </aside>
  );
}

/**
 * Phase 9 Session A — sub-component that consumes `useAdminCounts()`.
 * Isolated so the polling hook is only mounted for users with an admin
 * role; non-admins would otherwise be sent to `/signin` when the admin-gated
 * counts endpoints return 401.
 */
function AdminBadgedNavItem({ item }: { item: NavItem }) {
  const counts = useAdminCounts();
  const count = item.badgeKey ? counts[item.badgeKey] : null;
  const showBadge = typeof count === 'number' && count > 0;
  return (
    <NavLink to={item.to} className="hub-nav-item">
      <i className={`bx ${item.icon}`} aria-hidden="true" />
      <span className="flex-1">{item.label}</span>
      {showBadge && (
        <span
          data-testid={`sidebar-badge-${item.badgeKey}`}
          className="rounded-full bg-[var(--hub-color-danger)] px-2 py-0.5 text-xs font-medium text-[var(--hub-color-ink-on-action)]"
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}
